// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { baseGridHiddenProps } from "@store/Themes/common/props/baseGrid";
import constants from "@store/constants";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { infoProps } from "@store/Themes/common/props/info";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { usePageLinkByNodeId, usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showContactLink = "showContactLink",
    contactLinkHookText = "contactLinkHookText",
    contactLink = "contactLink",
}

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> & {
        fields: {
            [fields.showContactLink]: boolean;
            [fields.contactLinkHookText]: string;
            [fields.contactLink]: LinkFieldValue;
        };
    };

export interface FeatureNotesPageViewStyles {
    publishDateText?: TypographyProps;
    releaseInfoWrapper?: InjectableCss;
    label?: TypographyProps;
    value?: TypographyProps;
    infoGridItem?: GridItemProps;
    typeTag: InjectableCss;
    container?: GridContainerProps;
    leftColumn?: GridItemProps;
    rightColumn?: GridItemProps;
    contactMsgText?: TypographyProps;
}

export const featureNotesPageViewStyles: FeatureNotesPageViewStyles = {
    releaseInfoWrapper: {
        css: css`
            ${spacingStyles()}
            ${boxStyles(null)}
            background-color: ${getColor("common.background")};
            color: inherit;
            padding: ${pxToRem(32)};
        `,
    },
    infoGridItem: infoProps.grid?.fullItem,
    label: infoProps.label,
    value: infoProps.value,
    typeTag: mergeProps<FeatureNotesPageViewStyles["typeTag"]>(infoProps.value, {
        css: css`
            display: inline-block;
            border-radius: ${pxToRem(50)};
            padding: ${pxToRem(3, 16)};
        `,
    }),
    container: baseGridHiddenProps.container,
    leftColumn: baseGridHiddenProps.sideColumn,
    rightColumn: baseGridHiddenProps.mainColumn,
};

const styles = featureNotesPageViewStyles;

const FeatureNotesPageView = ({ id, page, fields: { showContactLink, contactLinkHookText, contactLink } }: Props) => {
    const contactLinkUrl = usePageLinkByNodeId(contactLink.value);

    if (!page) {
        return null;
    }

    const versionNumberBlockStyles = (page: PageProps) => {
        switch (page.fields["releaseType"]) {
            case "Minor":
                return {
                    css: css`
                        ${styles.typeTag?.css}
                        background-color: ${getColor("primary.alt")};
                        color: ${getColor("primary.contrast")};
                    `,
                };
            case "Major":
                return {
                    css: css`
                        ${styles.typeTag?.css}
                        background-color: ${getColor("secondary.alt")};
                        color: ${getColor("neutral.contrast")};
                    `,
                };
            case "Hotfix":
                return {
                    css: css`
                        ${styles.typeTag?.css}
                        background-color: ${getColor("tertiary.alt")};
                        color: ${getColor("tertiary.contrast")};
                    `,
                };
        }
    };

    // for some unknown reason creating a useless function to translate the text allows the linter to see the disable comment...
    const translatingDynamicData = (data: string) => {
        // eslint-disable-next-line spire/avoid-dynamic-translate
        return translate(data);
    };

    return (
        <GridContainer {...styles.container} key={page.id}>
            <GridItem {...styles.leftColumn}>
                <StyledWrapper {...styles.releaseInfoWrapper}>
                    <GridContainer>
                        {page.fields["publishDate"] && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.label}>{translate("Release Date")}</Typography>
                                <Typography {...styles.value}>
                                    <LocalizedDateTime
                                        dateTime={new Date(page.fields["publishDate"])}
                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                    />
                                </Typography>
                            </GridItem>
                        )}
                        {page.fields["releaseType"] && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.label}>{translate("Release Type")}</Typography>
                                <Typography {...versionNumberBlockStyles(page)}>
                                    {translatingDynamicData(page.fields["releaseType"])}
                                </Typography>
                            </GridItem>
                        )}
                        {showContactLink && contactLinkUrl && (
                            <GridItem {...styles.infoGridItem}>
                                <Typography {...styles.contactMsgText}>
                                    {`${contactLinkHookText} `}
                                    <Link href={contactLinkUrl?.url}>{translate("Let us know")}.</Link>
                                </Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.rightColumn}>
                <Zone contentId={id} zoneName="releaseNotesDetails" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(FeatureNotesPageView),
    definition: {
        group: constants.widgetGroup.featureNotes,
        allowedContexts: [constants.pageType.featureNotes],
        fieldDefinitions: [
            {
                name: fields.showContactLink,
                editorTemplate: "CheckboxField",
                variant: "toggle",
                fieldType: "General",
                defaultValue: true,
            },
            {
                name: fields.contactLinkHookText,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Found something that doesn't work properly?",
            },
            {
                name: fields.contactLink,
                displayName: "Contact Us Page Link",
                editorTemplate: "LinkField",
                fieldType: "Translatable",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isVisible: x => x.fields.showContactLink,
            },
        ],
    },
};

export default widgetModule;
